import React, { useState, useEffect, useRef, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import Card from '../components/ui/Card';
import Button from '../components/ui/Button';
import Modal from '../components/ui/Modal';
import ConfirmDialog from '../components/ui/ConfirmDialog';
import { useToast } from '../components/ui/Toast';
import SafeIcon from '../components/common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiUpload, FiShuffle, FiRefreshCw, FiX, FiCopy, FiCheck, FiPlay, FiTarget, FiClock, FiAlertTriangle, FiTrash2, FiLayers } = FiIcons;

const HERO_IMAGE = "https://cdn.midjourney.com/3175d814-e310-4002-a179-cd195d0a683c/0_0.webp";
const STORAGE_KEY = 'mindgarden-training-cards-v2';

const CATEGORIES = [
  { key: 'boxing', label: 'Boxing', color: '#D14D4D' },
  { key: 'jiu-jitsu', label: 'Jiu-Jitsu', color: '#2F6B4B' },
  { key: 'calisthenics', label: 'Calisthenics', color: '#E2A93B' },
  { key: 'fun-movement', label: 'Fun & Movement', color: '#6DAEDB' },
  { key: 'other', label: 'Other', color: '#B79AD0' }
];

// CSV parsing utility
const parseCSV = (csvText) => {
  const lines = csvText.trim().split('\n');
  if (lines.length < 2) return [];
  
  const headers = lines[0].split(',').map(h => h.trim().replace(/"/g, ''));
  const cards = [];

  for (let i = 1; i < lines.length; i++) {
    const values = lines[i].split(',').map(v => v.trim().replace(/"/g, ''));
    if (values.length < headers.length) continue;

    const card = {};
    headers.forEach((header, index) => {
      card[header.toLowerCase().replace(/\s+/g, '_')] = values[index] || '';
    });

    const normalizedCard = {
      card_id: card.card_id || card.id || `card_${Date.now()}_${i}`,
      category: normalizeCategory(card.category || ''),
      title: card.title || '',
      coach_voice: card.coach_voice || card.coach || '',
      drill: card.drill || card.description || '',
      duration: card.duration || '',
      intensity_1to5: normalizeIntensity(card.intensity_1to5 || card.intensity || '3'),
      equipment: card.equipment || '',
      tip: card.tip || card.tips || '',
      safety_note: card.safety_note || card.safety || '',
      image_prompt: card.image_prompt || card.prompt || ''
    };

    if (normalizedCard.title && normalizedCard.category) {
      cards.push(normalizedCard);
    }
  }
  return cards;
};

const normalizeCategory = (category) => {
  const cat = category.toLowerCase().trim();
  if (cat.includes('boxing')) return 'boxing';
  if (cat.includes('jiu') || cat.includes('bjj') || cat.includes('grappling')) return 'jiu-jitsu';
  if (cat.includes('calisthenics') || cat.includes('bodyweight')) return 'calisthenics';
  if (cat.includes('fun') || cat.includes('movement') || cat.includes('dance')) return 'fun-movement';
  return 'other';
};

const normalizeIntensity = (intensity) => {
  const num = parseInt(intensity);
  return (num >= 1 && num <= 5) ? num : 3;
};

export default function TrainingCards() {
  const [allDecks, setAllDecks] = useState([]);
  const [currentHand, setCurrentHand] = useState([]);
  const [drawnCardIds, setDrawnCardIds] = useState(new Set());
  const [selectedDeck, setSelectedDeck] = useState('all');
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [showDeleteAllConfirm, setShowDeleteAllConfirm] = useState(false);
  const [deckToDelete, setDeckToDelete] = useState(null);
  const [copiedPrompts, setCopiedPrompts] = useState(new Set());
  const [drawingAnimationCards, setDrawingAnimationCards] = useState([]);
  const fileInputRef = useRef();
  const { success, error, warning } = useToast();

  useEffect(() => {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved) {
      try {
        const data = JSON.parse(saved);
        setAllDecks(data.allDecks || []);
        setCurrentHand(data.currentHand || []);
        if (data.drawnCardIds) {
          setDrawnCardIds(new Set(data.drawnCardIds));
        } else {
          setDrawnCardIds(new Set((data.currentHand || []).map(c => c.card_id)));
        }
      } catch (e) {
        console.error('Failed to parse training cards data:', e);
      }
    }
  }, []);

  useEffect(() => {
    const data = {
      allDecks,
      currentHand,
      drawnCardIds: Array.from(drawnCardIds)
    };
    localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
  }, [allDecks, currentHand, drawnCardIds]);

  const totalCardsInDecks = useMemo(() => {
    return allDecks.reduce((sum, deck) => sum + deck.cards.length, 0);
  }, [allDecks]);

  const deckToDrawFrom = useMemo(() => {
    let sourceCards = [];
    if (selectedDeck === 'all') {
      sourceCards = allDecks.flatMap(deck => deck.cards);
    } else {
      const deck = allDecks.find(d => d.fileName === selectedDeck);
      sourceCards = deck ? deck.cards : [];
    }
    return sourceCards.filter(card => !drawnCardIds.has(card.card_id));
  }, [allDecks, selectedDeck, drawnCardIds]);

  const handleFileUpload = (event) => {
    const files = Array.from(event.target.files);
    if (!files.length) return;

    files.forEach(file => {
      if (file.type !== 'text/csv' && !file.name.endsWith('.csv')) {
        error(`${file.name} is not a valid training card file.`);
        return;
      }

      const reader = new FileReader();
      reader.onload = (e) => {
        try {
          const csvText = e.target.result;
          const newCards = parseCSV(csvText);
          const fileName = file.name;

          if (newCards.length === 0) {
            warning(`No valid cards found in ${fileName}`);
            return;
          }

          setAllDecks(prevDecks => {
            if (prevDecks.some(d => d.fileName === fileName)) {
              warning(`Card set "${fileName}" has already been uploaded. Skipped.`);
              return prevDecks;
            }
            
            const existingIds = new Set(prevDecks.flatMap(d => d.cards).map(c => c.card_id));
            const uniqueNewCards = newCards.filter(card => !existingIds.has(card.card_id));

            if (uniqueNewCards.length < newCards.length) {
              warning(`${newCards.length - uniqueNewCards.length} duplicate card IDs skipped from ${fileName}`);
            }

            if (uniqueNewCards.length > 0) {
              success(`Added ${uniqueNewCards.length} cards from ${fileName}`);
              return [...prevDecks, { fileName, cards: uniqueNewCards }];
            } else {
              warning(`All cards from ${fileName} were duplicates. Card set not added.`);
              return prevDecks;
            }
          });
        } catch (err) {
          error(`Failed to parse ${file.name}: ${err.message}`);
        }
      };
      reader.readAsText(file);
    });
    
    setShowUploadModal(false);
    event.target.value = '';
  };

  const drawCards = (count = 1) => {
    if (deckToDrawFrom.length === 0) {
      warning('No cards available to draw from the selected set.');
      return;
    }

    if (deckToDrawFrom.length < count) {
      warning(`Only ${deckToDrawFrom.length} cards available to draw.`);
      count = deckToDrawFrom.length;
    }

    setDrawingAnimationCards(Array.from({ length: count }));

    setTimeout(() => {
      const shuffled = [...deckToDrawFrom].sort(() => Math.random() - 0.5);
      const drawn = shuffled.slice(0, count);

      setDrawnCardIds(prevIds => {
        const newIds = new Set(prevIds);
        drawn.forEach(card => newIds.add(card.card_id));
        return newIds;
      });

      setDrawingAnimationCards([]);
      setCurrentHand(drawn);
      success(`Drew ${drawn.length} new card${drawn.length > 1 ? 's' : ''}!`);
    }, 1000);
  };

  const resetHand = () => {
    setCurrentHand([]);
    setDrawnCardIds(new Set());
    setCopiedPrompts(new Set());
    success('Card draw reset');
  };

  const clearAllCards = () => {
    setAllDecks([]);
    setCurrentHand([]);
    setDrawnCardIds(new Set());
    setCopiedPrompts(new Set());
    success('All training cards deleted');
    setShowDeleteAllConfirm(false);
  };

  const handleDeleteDeck = (fileName) => {
    setDeckToDelete(fileName);
  };

  const confirmDeleteDeck = () => {
    if (!deckToDelete) return;
    setAllDecks(prev => prev.filter(deck => deck.fileName !== deckToDelete));
    if (selectedDeck === deckToDelete) {
      setSelectedDeck('all');
    }
    success(`Card set "${deckToDelete.replace(/\.csv$/i, '')}" deleted.`);
    setDeckToDelete(null);
  };

  const copyImagePrompt = async (prompt, cardId) => {
    if (!prompt) {
      warning('No image prompt available');
      return;
    }
    try {
      await navigator.clipboard.writeText(prompt);
      setCopiedPrompts(prev => new Set([...prev, cardId]));
      success('Image prompt copied!');
      setTimeout(() => {
        setCopiedPrompts(prev => {
          const next = new Set(prev);
          next.delete(cardId);
          return next;
        });
      }, 3000);
    } catch (err) {
      error('Failed to copy to clipboard');
    }
  };

  const getCategoryColor = (category) => {
    return CATEGORIES.find(cat => cat.key === category)?.color || '#B79AD0';
  };

  const getIntensityColor = (intensity) => {
    const colors = ['#9BB898', '#6DAEDB', '#E2A93B', '#B79AD0', '#D14D4D'];
    return colors[intensity - 1] || '#9BB898';
  };

  return (
    <div className="space-y-6 relative">
      {/* Animation Overlay */}
      <AnimatePresence>
        {drawingAnimationCards.length > 0 && (
          <motion.div 
            className="fixed inset-0 flex items-center justify-center bg-black/40 backdrop-blur-sm z-50 pointer-events-none"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
          >
            <div className="relative w-64 h-96">
              {drawingAnimationCards.map((_, index) => (
                <motion.div
                  key={index}
                  className="absolute inset-0 m-auto w-48 h-72 bg-gradient-to-br from-beige via-white to-sage/10 rounded-2xl shadow-2xl border-4 border-sage/50 flex items-center justify-center"
                  style={{ originX: '50%', originY: '200%', backfaceVisibility: 'hidden' }}
                  initial={{ y: 300, scale: 0.5, opacity: 0, rotate: 0 }}
                  animate={{ 
                    y: -50, 
                    scale: 1, 
                    opacity: 1, 
                    rotate: (index - (drawingAnimationCards.length - 1) / 2) * 15,
                    transition: { delay: index * 0.08, type: 'spring', stiffness: 120, damping: 20 } 
                  }}
                  exit={{ y: 400, scale: 0.4, opacity: 0, rotate: 0, transition: { duration: 0.3, delay: index * 0.05 } }}
                >
                  <SafeIcon icon={FiShuffle} className="w-16 h-16 text-sage/50" />
                </motion.div>
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Hero Header */}
      <div className="relative h-48 lg:h-64 rounded-3xl overflow-hidden shadow-xl">
        <div className="absolute inset-0 bg-black/40 z-10" />
        <img 
          src={HERO_IMAGE} 
          alt="Training Deck" 
          className="w-full h-full object-cover" 
        />
        <div className="absolute inset-0 z-20 flex flex-col justify-end p-6 lg:p-8">
          <h1 className="text-3xl lg:text-4xl font-cormorant font-bold text-white mb-2 text-shadow-sm">
            Training Cards
          </h1>
          <p className="text-white/90 font-dm-sans max-w-lg">
            Randomize your growth. Draw cards to discover new challenges, exercises, and drills.
          </p>
        </div>
      </div>

      {/* Control Panel */}
      <Card className="p-4 lg:p-6 bg-white/90 backdrop-blur-md sticky top-16 z-10 shadow-md">
        <div className="space-y-4">
          <div className="flex flex-col lg:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-4 text-sm text-charcoal/70 font-dm-sans w-full lg:w-auto">
              <div className="flex items-center gap-2 bg-sage/10 px-3 py-1.5 rounded-lg border border-sage/20">
                <SafeIcon icon={FiLayers} className="w-4 h-4 text-sage" />
                <span>Pool: <strong className="text-charcoal">{deckToDrawFrom.length}</strong></span>
              </div>
              <div className="flex items-center gap-2 bg-goldenrod/10 px-3 py-1.5 rounded-lg border border-goldenrod/20">
                <SafeIcon icon={FiTarget} className="w-4 h-4 text-goldenrod" />
                <span>Hand: <strong className="text-charcoal">{currentHand.length}</strong></span>
              </div>
            </div>

            <div className="flex flex-wrap items-center gap-2 w-full lg:w-auto justify-end">
              <Button variant="primary" onClick={() => drawCards(1)} disabled={deckToDrawFrom.length === 0} icon={FiShuffle} size="sm">
                Draw 1
              </Button>
              <Button variant="primary" onClick={() => drawCards(5)} disabled={deckToDrawFrom.length === 0} icon={FiShuffle} size="sm">
                Draw 5
              </Button>
              <div className="h-6 w-px bg-gray-300 mx-1 hidden lg:block" />
              <Button variant="secondary" onClick={resetHand} icon={FiRefreshCw} size="sm">
                Reset
              </Button>
              <Button variant="secondary" onClick={() => setShowUploadModal(true)} icon={FiUpload} size="sm">
                Upload
              </Button>
              {allDecks.length > 0 && (
                <Button variant="danger" onClick={() => setShowDeleteAllConfirm(true)} icon={FiTrash2} size="sm">
                  Clear
                </Button>
              )}
            </div>
          </div>

          <div className="border-t border-sage/10 pt-3">
            <label className="block text-xs font-bold text-charcoal/50 uppercase tracking-wider mb-2">
              Active Deck
            </label>
            <div className="flex flex-wrap gap-2">
              <button 
                onClick={() => setSelectedDeck('all')}
                className={`
                  px-3 py-1.5 rounded-lg text-xs font-medium transition-all
                  ${selectedDeck === 'all' 
                    ? 'bg-charcoal text-white shadow-md' 
                    : 'bg-gray-100 text-charcoal/70 hover:bg-gray-200'}
                `}
              >
                All Cards ({totalCardsInDecks})
              </button>
              {allDecks.map(deck => {
                const deckName = deck.fileName.replace(/\.csv$/i, '');
                return (
                  <div key={deck.fileName} className="relative group">
                    <button 
                      onClick={() => setSelectedDeck(deck.fileName)}
                      className={`
                        pl-3 pr-8 py-1.5 rounded-lg text-xs font-medium transition-all max-w-[200px] truncate
                        ${selectedDeck === deck.fileName 
                          ? 'bg-sage text-white shadow-md' 
                          : 'bg-gray-100 text-charcoal/70 hover:bg-gray-200'}
                      `}
                    >
                      {deckName}
                    </button>
                    <button 
                      onClick={(e) => { e.stopPropagation(); handleDeleteDeck(deck.fileName); }}
                      className="absolute right-1 top-1/2 -translate-y-1/2 p-1 text-white/50 hover:text-white hover:bg-black/20 rounded-md transition-colors"
                    >
                      <SafeIcon icon={FiX} className="w-3 h-3" />
                    </button>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </Card>

      {/* Card Grid */}
      <div className="min-h-[400px]">
        {currentHand.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
            <AnimatePresence>
              {currentHand.map((card) => (
                <TrainingCard 
                  key={card.card_id} 
                  card={card} 
                  onCopyPrompt={copyImagePrompt} 
                  isCopied={copiedPrompts.has(card.card_id)}
                  getCategoryColor={getCategoryColor}
                  getIntensityColor={getIntensityColor}
                />
              ))}
            </AnimatePresence>
          </div>
        ) : (
          <div className="flex flex-col items-center justify-center py-20 text-center opacity-60">
            <div className="w-24 h-24 bg-sage/20 rounded-full flex items-center justify-center mb-6">
              <SafeIcon icon={FiLayers} className="w-10 h-10 text-sage" />
            </div>
            <h3 className="text-2xl font-cormorant font-bold text-charcoal mb-2">
              {allDecks.length === 0 ? 'Deck Empty' : 'Ready to Draw'}
            </h3>
            <p className="text-charcoal/60 font-dm-sans max-w-sm">
              {allDecks.length === 0 
                ? 'Upload a CSV file to populate your training deck.' 
                : 'Your deck is shuffled and ready for a new session.'}
            </p>
            {allDecks.length === 0 && (
              <Button variant="primary" onClick={() => setShowUploadModal(true)} icon={FiUpload} className="mt-6">
                Upload CSV
              </Button>
            )}
          </div>
        )}
      </div>

      <Modal isOpen={showUploadModal} onClose={() => setShowUploadModal(false)} title="Upload Training Cards" size="md">
        <div className="p-6 space-y-6">
          <div className="text-center">
            <div className="w-16 h-16 bg-sage/10 rounded-full flex items-center justify-center mx-auto mb-4">
              <SafeIcon icon={FiUpload} className="w-8 h-8 text-sage" />
            </div>
            <p className="text-charcoal/70 font-dm-sans">
              Upload your training cards (CSV format) to add them to the randomizer.
            </p>
          </div>
          
          <div 
            className="border-2 border-dashed border-sage/30 rounded-2xl p-8 text-center cursor-pointer hover:border-sage/50 hover:bg-sage/5 transition-all group"
            onClick={() => fileInputRef.current?.click()}
          >
            <SafeIcon icon={FiUpload} className="w-12 h-12 text-sage/40 mx-auto mb-4 group-hover:scale-110 transition-transform" />
            <p className="text-charcoal font-dm-sans font-bold mb-1">Click to select files</p>
            <p className="text-xs text-charcoal/50">Supports multiple .csv files</p>
          </div>
          <input 
            ref={fileInputRef} 
            type="file" 
            accept=".csv" 
            multiple 
            onChange={handleFileUpload} 
            className="hidden" 
          />
          
          <div className="flex pt-2">
            <Button variant="secondary" onClick={() => setShowUploadModal(false)} className="w-full">
              Cancel
            </Button>
          </div>
        </div>
      </Modal>

      <ConfirmDialog 
        isOpen={showDeleteAllConfirm} 
        onClose={() => setShowDeleteAllConfirm(false)} 
        onConfirm={clearAllCards}
        title="Delete All Training Cards" 
        message={`Are you sure you want to delete all ${totalCardsInDecks} training cards? This will also clear your current hand and cannot be undone.`}
        confirmText="Delete All Cards"
        cancelText="Keep Cards"
        variant="danger"
      />

      <ConfirmDialog 
        isOpen={!!deckToDelete} 
        onClose={() => setDeckToDelete(null)} 
        onConfirm={confirmDeleteDeck}
        title="Delete Card Set" 
        message={`Are you sure you want to delete the card set "${deckToDelete?.replace(/\.csv$/i, '')}"? This cannot be undone.`}
        confirmText="Delete Set"
        cancelText="Cancel"
        variant="danger"
      />
    </div>
  );
}

function TrainingCard({ card, onCopyPrompt, isCopied, getCategoryColor, getIntensityColor }) {
  return (
    <motion.div 
      layout 
      initial={{ opacity: 0, scale: 0.9, y: 20 }} 
      animate={{ opacity: 1, scale: 1, y: 0 }} 
      exit={{ opacity: 0, scale: 0.9 }} 
      transition={{ duration: 0.4, type: "spring", bounce: 0.3 }}
      className="h-full"
    >
      <Card className="p-0 h-full flex flex-col overflow-hidden border-t-4 shadow-lg hover:shadow-xl transition-shadow" style={{ borderTopColor: getCategoryColor(card.category) }}>
        <div className="p-5 flex-1 flex flex-col">
          {/* Header Tags */}
          <div className="flex flex-wrap gap-2 mb-4">
            <span 
              className="px-2.5 py-1 rounded-md text-[10px] uppercase font-bold tracking-wider text-white" 
              style={{ backgroundColor: getCategoryColor(card.category) }}
            >
              {card.category.replace('-', ' ')}
            </span>
            <span 
              className="px-2.5 py-1 rounded-md text-[10px] uppercase font-bold tracking-wider text-white"
              style={{ backgroundColor: getIntensityColor(card.intensity_1to5) }}
            >
              Intensity {card.intensity_1to5}
            </span>
          </div>

          <h3 className="text-xl font-cormorant font-bold text-charcoal mb-3 leading-snug">
            {card.title}
          </h3>

          {/* Coach Quote */}
          {card.coach_voice && (
            <div className="mb-4 p-3 bg-sage/10 rounded-xl border border-sage/10 relative">
              <SafeIcon icon={FiPlay} className="absolute top-3 left-3 w-4 h-4 text-goldenrod" />
              <p className="text-charcoal/80 font-dm-sans text-sm italic pl-6">
                "{card.coach_voice}"
              </p>
            </div>
          )}

          {/* Drill Details */}
          <div className="space-y-4 text-sm text-charcoal/80 font-dm-sans flex-grow">
            <div>
              <strong className="block text-charcoal mb-1 text-xs uppercase tracking-wide">The Drill</strong>
              <p className="leading-relaxed">{card.drill}</p>
            </div>
            
            {(card.equipment || card.tip) && (
              <div className="grid grid-cols-2 gap-4 pt-2">
                {card.equipment && (
                  <div>
                    <strong className="block text-charcoal mb-1 text-xs uppercase tracking-wide">Gear</strong>
                    <p>{card.equipment}</p>
                  </div>
                )}
                {card.tip && (
                  <div>
                    <strong className="block text-charcoal mb-1 text-xs uppercase tracking-wide">Pro Tip</strong>
                    <p>{card.tip}</p>
                  </div>
                )}
              </div>
            )}

            {card.safety_note && (
              <div className="flex items-start gap-2 pt-2 text-crimson text-xs bg-crimson/5 p-2 rounded-lg">
                <SafeIcon icon={FiAlertTriangle} className="w-4 h-4 flex-shrink-0 mt-0.5" />
                <span>{card.safety_note}</span>
              </div>
            )}
          </div>
        </div>

        {/* Footer Actions */}
        {card.image_prompt && (
          <div className="p-4 bg-gray-50 border-t border-gray-100">
            <button 
              onClick={() => onCopyPrompt(card.image_prompt, card.card_id)}
              className="w-full flex items-center justify-between group p-2 rounded-lg hover:bg-white transition-colors"
            >
              <div className="flex items-center gap-2 text-xs font-bold text-charcoal/60 uppercase tracking-wider group-hover:text-skyblue">
                <SafeIcon icon={FiCopy} className="w-3 h-3" />
                Midjourney Prompt
              </div>
              {isCopied && <SafeIcon icon={FiCheck} className="w-4 h-4 text-sage" />}
            </button>
          </div>
        )}
      </Card>
    </motion.div>
  );
}